import { BookingSlotPickerController } from '@application/components';
import { PRO_PAGE_ROUTE_PER_PRO_TYPE } from '@application/constants';
import { translatedFieldWithFallback, useModal, useRouter } from '@application/utils';
import type { Booking } from '@gql/graphql';
import type { MtBookingCardDataBooking } from '@presentation/components';
import { ConfirmationModal, LoadingOverlay, MtBookingUpdateModal } from '@presentation/components';
import { MtBookingsPage } from '@presentation/pages';
import dayjs from 'dayjs';
import { partition } from 'lodash/fp';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { BookingFormPrefillValues } from '../../BookingSlotPickerController/types';
import { useDeleteBooking } from './api/useDeleteBooking';
import { useGetBookings } from './api/useGetBookings';
import { useUpdateBooking } from './api/useUpdateBooking';

export const MtBookingsPageController: FC = () => {
  const { modal: modalDelete, show: showDelete, close: closeDelete } = useModal();
  const [deleteBookingId, setDeleteBookingId] = useState<number | undefined>(undefined);

  const { modal: modalUpdate, show: showUpdate, close: closeUpdate } = useModal();
  const [updateBookingId, setUpdateBookingId] = useState<number | undefined>(undefined);

  const { language, translate, navigateToRoute, routeLocationDescriptor } = useRouter();
  const { bookings, bookingsLoading } = useGetBookings();
  const { updateBooking, updateBookingLoading } = useUpdateBooking();
  const { deleteBooking, deleteBookingLoading } = useDeleteBooking();

  const bookingToUpdate = useMemo(() => {
    return bookings.find((x) => x.id === updateBookingId);
  }, [bookings, updateBookingId]);

  const prefillValues = useMemo((): BookingFormPrefillValues | undefined => {
    if (!bookingToUpdate) return;
    const animals = bookingToUpdate.bookingAnimals.map((x) => x.animal);
    return {
      forMultiAnimals: animals.length > 1,
      animalType: animals[0]?.type ?? undefined,
      breedId: animals[0]?.dogInfos?.dogBreedId ?? undefined,
      workerId: bookingToUpdate?.worker.id,
      workerServiceId: bookingToUpdate?.workerService.id,
      proServiceId: `${bookingToUpdate.workerService.categoryId}_${bookingToUpdate.workerService.service.id}`,
      proId: bookingToUpdate?.worker.pro.id,
    };
  }, [bookingToUpdate]);

  const formatBooking = useCallback(
    (booking: Booking): MtBookingCardDataBooking => {
      const { pro } = booking.worker;
      const service =
        translatedFieldWithFallback(
          language,
          booking.workerService.service.translations,
          'title',
        ) ?? '';
      const url = translatedFieldWithFallback(language, pro.translations, 'url') ?? '';
      if (!url) {
        // eslint-disable-next-line no-console
        console.warn('Pro url is empty');
      }
      let warning: string | undefined;
      let deadlineHasPassed = false;
      if (pro.minHoursBeforeUpdateOrCancelBooking) {
        const bookingToDeleteStart = dayjs(booking.startDateTime);
        if (
          bookingToDeleteStart
            .subtract(pro.minHoursBeforeUpdateOrCancelBooking, 'hours')
            .isBefore(dayjs())
        ) {
          deadlineHasPassed = true;
        }
        if (deadlineHasPassed) {
          warning = translate('mt.buyer.booking.warning.after');
        } else {
          const formattedTime = pro.minHoursBeforeUpdateOrCancelBooking.toString();
          warning = translate('mt.buyer.booking.warning.before').replace('$time', formattedTime);
        }
      }
      return {
        id: booking.id,
        service,
        warning,
        canUpdate: !deadlineHasPassed,
        startDate: dayjs(booking.startDateTime),
        endDate: dayjs(booking.endDateTime),
        duration: `${dayjs
          .duration(dayjs(booking.endDateTime).diff(booking.startDateTime))
          .asMinutes()}min`,
        animals: booking.bookingAnimals.map(({ animal }) => ({
          link: routeLocationDescriptor('mtAnimals', { params: [animal.id.toString()] }),
          name: animal.name ?? '',
          gender: animal.gender ?? undefined,
          image: animal.imgs?.[0]?.small ?? undefined,
          birthday: animal.birthday ? dayjs(animal.birthday) : undefined,
        })),
        pro: {
          name: booking.worker.fullName ?? '',
          email: pro.email ?? undefined,
          phone: pro.phone ?? pro.mobilePhone ?? undefined,
          link: routeLocationDescriptor(PRO_PAGE_ROUTE_PER_PRO_TYPE[pro.type], { params: [url] }),
          image: pro.imgs?.[0]?.small ?? undefined,
        },
      };
    },
    [language, routeLocationDescriptor, translate],
  );

  const now = dayjs();
  const partitionnedBookings = partition(
    (booking) => dayjs(booking.startDateTime).isAfter(now),
    bookings,
  );
  return (
    <>
      <ConfirmationModal
        title={translate('common.modals.cancelAppointment.title')}
        text={translate('common.modals.cancelAppointment.text')}
        onConfirm={async () => {
          await deleteBooking(deleteBookingId);
          closeDelete();
        }}
        modal={modalDelete}
      />
      <MtBookingUpdateModal
        modal={modalUpdate}
        slotPickerComponent={
          <BookingSlotPickerController
            updateMode
            customFormTitle={translate('mt.buyer.booking.slotPicker.update.title')}
            prefillValues={prefillValues}
            customSubmit={async (values) => {
              if (!bookingToUpdate?.id) return;
              await updateBooking({
                bookingId: bookingToUpdate?.id,
                bookingStart: dayjs(values.bookingStart),
              });
              closeUpdate();
            }}
          />
        }
      />
      <LoadingOverlay transparent isLoading={deleteBookingLoading || updateBookingLoading} />
      <MtBookingsPage
        loading={bookingsLoading}
        onDeleteBookingClick={(bookingId) => {
          setDeleteBookingId(bookingId);
          showDelete();
        }}
        onUpdateBookingClick={(bookingId) => {
          setUpdateBookingId(bookingId);
          showUpdate();
        }}
        onProListClick={() => navigateToRoute('proLanding')}
        items={partitionnedBookings[1].map((x) => formatBooking(x))}
        upcomingItems={partitionnedBookings[0].map((x) => formatBooking(x))}
      />
    </>
  );
};
